import {
  Button,
  ButtonGroup,
  Card,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Search,
  Segment,
  showNotification,
  Table,
  TableRow,
} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useState} from 'react';

import {descend, head, sortWith} from 'ramda';

import {
  EmployeeWarehouseAccessRangeSchema,
  useGetEmployeeWarehouseAccessQuery,
  usePutEmployeeWarehouseAccessMutation,
} from '@dms/api/customer';
import {useGetWarehousesQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useEmployeeUrl} from '../../hooks/useEmployeeUrl';
import {WarehouseAccessLastUpdate} from './components/WarehouseAccessLastUpdate';
import {
  filterWarehouseAccessRows,
  WarehouseAccessRangeFilter,
} from './utils/filterWarehouseAccessRows';
import {getWarehouseAccessRows, WarehouseAccessRange} from './utils/getWarehouseAccessRows';

export function WarehouseAccess(props: TestIdProps) {
  const {employeeId} = useEmployeeUrl();
  const [searchString, setSearchString] = useState<string | null>(null);
  const [accessRangeFilter, setAccessRangeFilter] = useState<WarehouseAccessRangeFilter>('ALL');

  // v1 on purpose: v2 leaves out the warehouses the logged-in admin cannot read
  const warehousesQuery = useGetWarehousesQuery();
  const warehouseAccessQuery = useGetEmployeeWarehouseAccessQuery({employeeId});
  const [putEmployeeWarehouseAccess] = usePutEmployeeWarehouseAccessMutation();

  const warehouseAccess = warehouseAccessQuery.data?.warehouseAccess ?? [];
  const rows = getWarehouseAccessRows(warehousesQuery.data ?? [], warehouseAccess);
  const visibleRows = filterWarehouseAccessRows(rows, searchString, accessRangeFilter);
  // The GET does not define the row order, so take the latest save explicitly
  const lastUpdate = head(sortWith([descend((access) => access.modifiedAt)], warehouseAccess));

  const formValues: WarehouseAccessForm = {
    warehouseAccess: rows.map((row) => ({
      warehouseId: row.warehouseId,
      accessRange: row.accessRange,
    })),
  };

  const accessRangeOptions: {label: string; value: WarehouseAccessRange}[] = [
    {label: i18n.t('general.labels.unassigned'), value: 'NONE'},
    {label: i18n.t('page.employeeDetail.labels.warehouseAccessRead'), value: 'READ'},
    {label: i18n.t('general.labels.edit'), value: 'EDIT'},
  ];

  const accessRangeFilterOptions: {label: string; value: WarehouseAccessRangeFilter}[] = [
    {label: i18n.t('general.labels.all'), value: 'ALL'},
    ...accessRangeOptions,
  ];

  const handleSubmit: FormSubmitHandler<WarehouseAccessForm> = async (values) => {
    await putEmployeeWarehouseAccess({
      employeeId,
      body: {
        warehouseAccess: values.warehouseAccess.flatMap((access) =>
          access.accessRange === 'NONE'
            ? []
            : [{warehouseId: access.warehouseId, accessRange: access.accessRange}]
        ),
      },
    })
      .unwrap()
      .then(() =>
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'))
      )
      .catch(handleApiError);
  };

  return (
    <DataStatus
      isLoading={warehousesQuery.isLoading || warehouseAccessQuery.isLoading}
      isError={warehousesQuery.isError || warehouseAccessQuery.isError}
      minHeight={140}
    >
      <Form<WarehouseAccessForm>
        experimentalZodSchema={schema}
        onSubmit={handleSubmit}
        values={formValues}
      >
        {(control, formApi) => (
          <VStack spacing={4}>
            <Card title={i18n.t('entity.warehouse.labels.warehouses')}>
              <VStack spacing={4}>
                <HStack spacing={4} align="center" justify="space-between">
                  <Box width={SEARCH_WIDTH}>
                    <Search
                      value={searchString}
                      onChange={setSearchString}
                      data-testid={suffixTestId('search', props)}
                    />
                  </Box>
                  <Segment<WarehouseAccessRangeFilter>
                    value={accessRangeFilter}
                    options={accessRangeFilterOptions}
                    onChange={setAccessRangeFilter}
                    data-testid={suffixTestId('accessRangeFilter', props)}
                  />
                </HStack>
                <Table
                  columns={[
                    {element: <ColumnTitle title={i18n.t('entity.warehouse.labels.number')} />},
                    {element: <ColumnTitle title={i18n.t('entity.warehouse.labels.name')} />},
                    {
                      element: <ColumnTitle title={i18n.t('general.labels.access')} />,
                      width: ACCESS_COLUMN_WIDTH,
                    },
                  ]}
                  data-testid={suffixTestId('table', props)}
                >
                  {visibleRows.map((row) => (
                    <TableRow
                      key={row.warehouseId}
                      data-testid={suffixTestId(`row-${row.warehouseId}`, props)}
                    >
                      <Box padding={2}>
                        <Text size="small">{row.warehouseNumber ?? EMPTY_PLACEHOLDER}</Text>
                      </Box>
                      <Box padding={2}>
                        <Text size="small">
                          {row.warehouseName ??
                            i18n.t('page.employeeDetail.labels.unknownWarehouse')}
                        </Text>
                      </Box>
                      <Box padding={1}>
                        <FormField
                          control={control}
                          type="choice"
                          name={`warehouseAccess.${row.index}.accessRange`}
                          options={accessRangeOptions}
                          isNotClearable
                          menuInPortal
                          data-testid={suffixTestId(`accessRange-${row.warehouseId}`, props)}
                        />
                      </Box>
                    </TableRow>
                  ))}
                </Table>
                <Show when={visibleRows.length === 0}>
                  <Text size="small" color="secondary" align="center">
                    {i18n.t('general.labels.noResultsFound')}
                  </Text>
                </Show>
                <Show when={lastUpdate}>
                  {(lastUpdate) => (
                    <WarehouseAccessLastUpdate
                      modifiedAt={lastUpdate.modifiedAt}
                      modifiedBy={lastUpdate.modifiedBy}
                      data-testid={props['data-testid']}
                    />
                  )}
                </Show>
              </VStack>
            </Card>
            <ButtonGroup align="right">
              <Button
                variant="secondary"
                title={i18n.t('general.actions.discard')}
                onClick={() => formApi.reset(formValues)}
                data-testid={suffixTestId('discard', props)}
              />
              <FormButton
                control={control}
                type="submit"
                variant="primary"
                title={i18n.t('general.actions.saveChanges')}
                data-testid={suffixTestId('save', props)}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}

function ColumnTitle(props: {title: string}) {
  return (
    <Box paddingHorizontal={2} paddingVertical={1}>
      <Text size="xSmall" color="secondary">
        {props.title}
      </Text>
    </Box>
  );
}

const ACCESS_COLUMN_WIDTH = 200;
const SEARCH_WIDTH = 60;

const schema = z.object({
  warehouseAccess: z.array(
    z.object({
      warehouseId: z.string(),
      accessRange: z.union([EmployeeWarehouseAccessRangeSchema, z.literal('NONE')]),
    })
  ),
});

type WarehouseAccessForm = z.infer<typeof schema>;
